import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertTriangle,
  Lightbulb,
  Wrench,
  Sparkles,
  Calendar,
  CalendarClock,
  CalendarCheck,
} from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Sprint 11.6 Block 2 — Task 2: HeartPieceV3 Rendering
// 3 Bausteine pro FT × 4 Ebenen (Diagnose, Ursache, Werkzeug, Lösung).
// Backend: GET /api/heart-piece/v3/{fokustyp}
//
// UX: Accordion — Default-Offen ist Baustein 1. Andere klappen auf Klick.
// Innerhalb eines Bausteins sind alle 4 Ebenen sichtbar (KEIN Sub-Accordion).
// Werkzeug-Sektion ist intern dreigeteilt: heute / diese_woche / 7-Tage-Reflexion.

const FT_NAMES = { eule: "Eule", biene: "Biene", katze: "Katze", hund: "Hund" };
const FT_ACCENT = {
  eule: "#3B82F6",   // blau
  biene: "#10B981",  // grün
  katze: "#FBBF24",  // amber
  hund: "#F97316",   // orange
};

function Section({ icon: Icon, label, children, accent, testid }) {
  return (
    <div className="mt-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <h4 className="text-xs font-bold uppercase tracking-widest" style={{ color: accent }}>
          {label}
        </h4>
      </div>
      <div className="text-sm text-slate-700 leading-relaxed">{children}</div>
    </div>
  );
}

function WerkzeugBlock({ baustein, accent }) {
  return (
    <div className="mt-4" data-testid="hpv3-werkzeug-block">
      <div className="flex items-center gap-2 mb-2">
        <Wrench className="w-4 h-4" style={{ color: accent }} />
        <h4 className="text-xs font-bold uppercase tracking-widest" style={{ color: accent }}>
          Werkzeug
        </h4>
      </div>
      <div className="space-y-2">
        <div className="rounded-lg border-l-4 bg-slate-50 px-3 py-2.5" style={{ borderColor: accent }}>
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            <Calendar className="w-3 h-3" />
            Heute
          </div>
          <p className="mt-1 text-sm text-slate-800 leading-relaxed">{baustein.werkzeug_heute}</p>
        </div>
        <div className="rounded-lg border-l-4 bg-slate-50 px-3 py-2.5" style={{ borderColor: accent }}>
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            <CalendarClock className="w-3 h-3" />
            Diese Woche
          </div>
          <p className="mt-1 text-sm text-slate-800 leading-relaxed">{baustein.werkzeug_woche}</p>
        </div>
        <div className="rounded-lg border-l-4 bg-slate-50 px-3 py-2.5" style={{ borderColor: accent }}>
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            <CalendarCheck className="w-3 h-3" />
            In 7 Tagen reflektieren
          </div>
          <p className="mt-1 text-sm text-slate-800 leading-relaxed">{baustein.werkzeug_7tage}</p>
        </div>
      </div>
    </div>
  );
}

function BausteinCard({ baustein, accent, isOpen, onToggle, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 * index }}
      className="rounded-2xl border bg-white shadow-sm overflow-hidden"
      style={{ borderColor: isOpen ? accent : "#E2E8F0" }}
      data-testid={`hpv3-baustein-${baustein.spielzug_nr}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-5 py-4 flex items-start gap-3 hover:bg-slate-50 transition-colors"
        data-testid={`hpv3-baustein-toggle-${baustein.spielzug_nr}`}
      >
        <span
          className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full text-sm font-bold text-white"
          style={{ background: accent }}
        >
          {baustein.spielzug_nr}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
            Flexpfad {baustein.spielzug_nr}
          </p>
          <h3 className="mt-0.5 text-base sm:text-lg font-bold text-slate-900 leading-snug">
            {baustein.titel}
          </h3>
        </div>
        <span className="shrink-0 mt-2 text-slate-400">
          {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 border-t" style={{ borderColor: `${accent}22` }}>
              <Section icon={AlertTriangle} label="Diagnose" accent={accent} testid={`hpv3-diagnose-${baustein.spielzug_nr}`}>
                {baustein.diagnose}
              </Section>
              <Section icon={Lightbulb} label="Ursache" accent={accent} testid={`hpv3-ursache-${baustein.spielzug_nr}`}>
                {baustein.ursache}
              </Section>
              <WerkzeugBlock baustein={baustein} accent={accent} />
              <Section icon={Sparkles} label="Lösung" accent={accent} testid={`hpv3-loesung-${baustein.spielzug_nr}`}>
                <strong className="font-semibold text-slate-900">{baustein.loesung}</strong>
              </Section>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-3" data-testid="hpv3-skeleton">
      {[1, 2, 3].map((i) => (
        <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 animate-pulse">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-slate-200" />
            <div className="flex-1">
              <div className="h-3 w-20 bg-slate-200 rounded" />
              <div className="mt-2 h-5 w-3/4 bg-slate-200 rounded" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function HeartPieceV3Page() {
  const { user } = useAuth();
  const [bausteine, setBausteine] = useState(null);
  const [openId, setOpenId] = useState(1);
  const [error, setError] = useState("");

  const ft = (user?.primary_fokustyp || user?.fokustyp_primary || "").toLowerCase();

  useEffect(() => {
    if (!ft) return;
    let cancelled = false;
    setBausteine(null);
    setError("");
    api
      .get(`/heart-piece/v3/${ft}`)
      .then((res) => {
        if (cancelled) return;
        setBausteine(Array.isArray(res.data?.bausteine) ? res.data.bausteine : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Konnte Flexpfad-Bibliothek nicht laden.");
      });
    return () => { cancelled = true; };
  }, [ft]);

  const ftName = FT_NAMES[ft] || null;
  const accent = FT_ACCENT[ft] || "#4563BB";

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="hpv3-page">
        {/* Sprint M5.40 (30.07.2026) — Block C1: Doppelte Rücknavigation
            entfernt. DashboardLayout stellt bereits `back-to-dashboard-bar` bereit. */}

        <div className="mb-6">
          <p className="text-xs font-bold uppercase tracking-widest" style={{ color: accent }} data-testid="hpv3-eyebrow">
            Fokustypen-Toolbox · 3 Kern-Werkzeuge für deinen Fokustyp
          </p>
          <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            {ftName ? `Fokustypen-Toolbox · Fokustyp ${ftName}` : "Deine Fokustypen-Toolbox"}
          </h1>
          <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed">
            Drei konkrete Werkzeuge, die du heute, diese Woche und nach 7 Tagen anwendest —
            jedes mit Diagnose, Ursache und einer klaren Lösung.
          </p>
        </div>

        {!ft && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="hpv3-no-ft">
            Dein Fokustyp ist noch nicht gesetzt — bitte starte zuerst den Quick-Check oder den Fokustyp-Test.
          </div>
        )}

        {ft && error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900" data-testid="hpv3-error">
            {error}
          </div>
        )}

        {ft && !error && bausteine === null && <Skeleton />}

        {ft && !error && Array.isArray(bausteine) && bausteine.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700" data-testid="hpv3-empty">
            Keine Bausteine für {ftName} verfügbar.
          </div>
        )}

        {ft && !error && Array.isArray(bausteine) && bausteine.length > 0 && (
          <div className="space-y-3" data-testid="hpv3-bausteine">
            {bausteine.map((b, i) => (
              <BausteinCard
                key={b.spielzug_nr}
                baustein={b}
                accent={accent}
                index={i}
                isOpen={openId === b.spielzug_nr}
                onToggle={() => setOpenId(openId === b.spielzug_nr ? null : b.spielzug_nr)}
              />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
